// EQ-1 — which physical machines the athlete has actually used, derived
// from history rather than kept in a list of their own.
//
// The source of truth is the set stream: an instance EXISTS because it was
// logged, not because a row somewhere says so. That is deliberate — a
// parallel registry is a denormalised snapshot that can drift from the
// data it claims to describe, and the equipment identity is exactly the
// kind of thing nobody would notice had drifted.
//
// Declared limit (Antenore, 2026-08-13): callers pass the window they
// already hold — in the logger that is useEntryHistory's last 50 sessions
// — so an instance unused for longer than that window does not appear.
// Recreating it under the same automatic name rejoins its history, because
// the identity IS the tag value, not a record.

import type { Workout } from "./schemas/workout.js";
import { heaviestWeightedSet } from "./workoutHistory.js";

/** The later of two ISO instants. Written once because this file merges "when
 *  was it last used" three different ways — globally, per place, and per
 *  location row — and a comparison phrased backwards in one of them is the
 *  kind of divergence nobody notices until two numbers disagree. */
const laterIso = (a: string, b: string): string => (a > b ? a : b);

export type KnownInstance = {
  /** The bare slug stored in `entry.equipmentInstance`. */
  value: string;
  /** Where it was most recently used, when the entry recorded a place. */
  location?: string;
  /**
   * ⭐ Recency PER PLACE — place slug → ISO instant it was last used there
   * (EQ-1-LASTHERE, his decision 2026-08-18). `location` above answers "where
   * was it last", which is one value and says nothing about anywhere else;
   * this answers "when was it last HERE", which is the question the silent
   * apply has to ask. Absent from the map ⇒ never used at that place, and
   * that is the whole travel case: the machine at home has no entry for the
   * gym you are visiting, so nothing gets attached there.
   *
   * Derived from the set stream like everything else in this model — no
   * table, no migration, "it exists because it was logged" (§9.5.2).
   */
  lastUsedHere?: Readonly<Record<string, string>>;
  /** ISO instant of the most recent session that used it. */
  lastUsedAt: string;
  /** How many sessions in the window used it — drives "which is the usual one". */
  sessions: number;
  /**
   * The heaviest weighted set of the most RECENT session on this instance,
   * in kg. This is how the athlete tells two machines apart while standing in
   * front of one (Antenore, 2026-08-13): four lat pulldowns cannot be
   * remembered by invented names, but "the one where I pull 80" is read off
   * the machine itself, with nothing to recall.
   *
   * It is not only a recognition aid. The whole reason equipment identity
   * exists is that two machines with different resistance must not pollute
   * one RPE/e1RM series — so the load IS the distinction, and showing it
   * surfaces the content of the split rather than a label standing in for it.
   *
   * Most recent rather than all-time best: it answers "what do I load on this
   * one", not "what is my record".
   */
  topSet?: { weightKg: number; reps: number };
};

// Canonical automatic names are ENGLISH slugs, never localised.
//
// Not a style choice, a correctness one, and it holds for one athlete or a
// million: a STORED IDENTITY must not depend on the language of whoever
// created it. Localise the slug and the same physical machine is minted as
// `macchina-a` for a reader in Italian and `machine-a` for a reader in
// French — two identities, one machine, and a history split exactly where
// this feature exists to keep it whole. The same athlete switching app
// language would do it to themselves.
//
// The label the athlete READS is localised by the UI; the value stored on
// the set is not. (The app ships five locales and its reference athletes
// already log in more than one, which is how the trap was noticed — but
// the rule does not depend on who the users happen to be.)
const AUTO_PREFIX = "machine";
const AUTO_SUFFIXES = "abcdefghijklmnopqrstuvwxyz".split("");

export const autoInstanceValue = (index: number): string =>
  `${AUTO_PREFIX}-${AUTO_SUFFIXES[index] ?? String(index + 1)}`;

/**
 * The letter of an automatic name (`machine-b` → `B`), or null when the value
 * is not one. Renaming is not in this slice, but a value that did not come
 * from `autoInstanceValue` must still render as itself rather than as a
 * mangled letter — the UI reads this to decide between the localised
 * "Machine {{letter}}" label and the bare stored value.
 */
export const instanceLetter = (value: string): string | null => {
  const match = /^machine-([a-z])$/.exec(value);
  return match ? match[1]!.toUpperCase() : null;
};

/** The lowest automatic name not already taken for this exercise. */
export const nextAutoInstanceValue = (
  taken: readonly string[],
): string => {
  const used = new Set(taken);
  for (let i = 0; i < AUTO_SUFFIXES.length; i++) {
    const candidate = autoInstanceValue(i);
    if (!used.has(candidate)) return candidate;
  }
  // Past 26 machines for one exercise the letters run out. Falling back to
  // a numeric tail keeps the function total rather than returning null and
  // pushing an impossible-in-practice branch onto every caller.
  return autoInstanceValue(taken.length);
};

// The machine nobody ever named, as one bucket of the walk below.
//
// ⛔ Unexported, and it must stay that way: this key exists to let ONE pass
// accumulate the unnamed entries under the same "later wins + topSet" rule as
// the named ones, and it is split back out before returning. It is not a
// value — nothing may ever store it on a set. `entry.equipmentInstance` stays
// `null` for the default, which is why choosing it needs no migration of
// anything: it writes the absence that was already there.
//
// ⚠️ Visibly not a slug, and visibly is the point: stored values are
// `[a-z0-9-]`, so the underscores cannot collide, AND the marker can be
// proof-read. The first version of this line used a leading space; a stray NUL
// rode into the source file in its place and went green through typecheck and
// 2787 tests, because nothing that checks source checks for what you cannot
// see. A sentinel has to be legible to be reviewable.
const UNNAMED = "__unnamed__";

/** What is known about an exercise's machines, named and unnamed alike. */
export type EquipmentInstanceSurvey = {
  /** The machines that were given a name, most recently used first. */
  named: KnownInstance[];
  /**
   * The default: everything logged before any machine had a name, collapsed
   * into ONE virtual instance.
   *
   * ⭐ Why it exists (Antenore, P0 2026-08-14): *"le attrezzature che uso
   * sempre non sono taggate, quindi se per errore taggo una nuova macchina per
   * la prima volta, non posso switchare alla mia default"*. The default was
   * invisible by construction — this walk used to skip untagged entries
   * outright — so naming a machine was a one-way door.
   *
   * One bucket, not a list: nobody ever distinguished these sessions from each
   * other, so claiming they are several machines would invent a split the data
   * does not contain. It carries a load signature like any other instance,
   * because the reason a machine is recognisable is the weight on it, and this
   * is the one the athlete has used most.
   *
   * ⛔ It has no `value`, deliberately. There is no slug to store, and a type
   * that cannot express one cannot leak a sentinel into a set.
   */
  unnamed?: Omit<KnownInstance, "value">;
};

/**
 * Everything the window knows about `exerciseId`'s machines: the named ones
 * (`named`, most recently used first) and the default (`unnamed`).
 *
 * ⛔ `named` is what decides whether the athlete is ASKED
 * (`resolveInstanceChoice`) and what gets applied in silence. The default must
 * never be folded back into it: it is always offered and never counted, so an
 * exercise with exactly one named machine stays silent instead of growing a
 * prompt. Flattening the two here would reintroduce that prompt by accident
 * rather than by decision.
 *
 * `opts.orderByLocation` brings the ones used at that place to the FRONT —
 * the second axis Antenore described (exercise x gym is the list of special
 * machines that exist there), but as a ranking, never as a filter.
 *
 * ⛔ It used to filter, and the rename is the point (Antenore, 2026-08-13):
 * *"essendo un grouping non impatta il logging, ma solo la scelta degli
 * esercizi… non deve bloccare la vista degli altri esercizi"*. A place that
 * HIDES a machine is the same dead end as the first-instance one found on
 * 13/08: you stand in front of a machine the app has decided does not exist
 * here, and there is no way forward. Ranking gives the whole benefit — what
 * is here reads first — and costs nothing when the guess about "here" is
 * wrong, because the rest is still one scroll away.
 *
 * The parameter is NAMED for what it does so a future reader cannot restore
 * the old meaning from the signature alone.
 */
export const surveyEquipmentInstances = (
  workouts: readonly Workout[],
  exerciseId: string,
  opts: { orderByLocation?: string } = {},
): EquipmentInstanceSurvey => {
  const byValue = new Map<string, KnownInstance>();
  for (const w of workouts) {
    for (const entry of w.entries) {
      if (entry.exerciseId !== exerciseId) continue;
      // ⛔ The untagged entries are bucketed, NOT skipped. Dropping them here
      // is what made the default unreachable, and re-deriving it in a second
      // pass would fork the "later wins + topSet" rule below into two copies
      // that could answer differently for the machine you use most.
      const value = entry.equipmentInstance || UNNAMED;
      const prev = byValue.get(value);
      const top = heaviestWeightedSet(entry);
      if (!prev) {
        byValue.set(value, {
          value,
          ...(entry.location ? { location: entry.location } : {}),
          ...(entry.location
            ? { lastUsedHere: { [entry.location]: w.startedAt } }
            : {}),
          lastUsedAt: w.startedAt,
          sessions: 1,
          ...(top ? { topSet: top } : {}),
        });
        continue;
      }
      // Same instance seen again: bump the count, and keep the LATER
      // instant plus the place that went with it. Workouts arrive in no
      // guaranteed order, so this compares rather than assuming.
      const isLater = w.startedAt > prev.lastUsedAt;
      // The signature follows the same "later wins" rule as the instant it
      // belongs to, and a session with no weighted set (bodyweight, timed)
      // does not erase the last one that had one.
      const topSet = isLater ? (top ?? prev.topSet) : (prev.topSet ?? top);
      // Per-place recency: keep the LATER instant for THIS place and leave
      // every other place untouched. Same compare-don't-assume rule as the
      // global instant above, because workouts arrive in no guaranteed order.
      const lastUsedHere = entry.location
        ? {
            ...prev.lastUsedHere,
            [entry.location]: laterIso(
              prev.lastUsedHere?.[entry.location] ?? '',
              w.startedAt,
            ),
          }
        : prev.lastUsedHere;
      byValue.set(value, {
        value,
        ...(isLater
          ? entry.location
            ? { location: entry.location }
            : {}
          : prev.location
            ? { location: prev.location }
            : {}),
        ...(lastUsedHere ? { lastUsedHere } : {}),
        lastUsedAt: isLater ? w.startedAt : prev.lastUsedAt,
        sessions: prev.sessions + 1,
        ...(topSet ? { topSet } : {}),
      });
    }
  }
  // Split the default back out before anything downstream can count it as a
  // machine. `value` goes with it: the type of what is returned cannot carry
  // the sentinel, so no caller can pass it on to be stored.
  const unnamedRow = byValue.get(UNNAMED);
  byValue.delete(UNNAMED);
  const unnamed = unnamedRow
    ? (({ value: _value, ...rest }) => rest)(unnamedRow)
    : undefined;

  // Recency is the base order. When a place is given it becomes the primary
  // key — used-here first — with recency breaking ties inside each half, so
  // the list stays stable and nothing is ever dropped.
  const here = opts.orderByLocation;
  const named = Array.from(byValue.values()).sort((a, b) => {
    if (here !== undefined) {
      // ⭐ EQ-1-LASTHERE: "used here" is now read from the per-place recency,
      // not from `location` — which only ever knew where an instance was used
      // LAST, so a machine you use here every week fell out of the group the
      // day you used it once somewhere else.
      const aHere = a.lastUsedHere?.[here];
      const bHere = b.lastUsedHere?.[here];
      if ((aHere === undefined) !== (bHere === undefined)) {
        return aHere === undefined ? 1 : -1;
      }
      // Inside the group, the one used most recently HERE leads. This is what
      // makes A→A′ readable without inventing a "replaces" relation: the new
      // machine rises the first time you use it here, the old one sinks.
      if (aHere !== undefined && bHere !== undefined && aHere !== bHere) {
        return bHere.localeCompare(aHere);
      }
    }
    return b.lastUsedAt.localeCompare(a.lastUsedAt);
  });
  return unnamed ? { named, unnamed } : { named };
};

/** A place the athlete has actually trained at, derived from the set stream. */
export type KnownLocation = {
  /** The bare slug stored in `entry.location`. */
  value: string;
  /** ISO instant of the most recent session that used it. */
  lastUsedAt: string;
  /** How many sessions in the window used it. */
  sessions: number;
};

/**
 * Places seen in the given window, most recently used first.
 *
 * Same no-registry rule as the machines above (§9.5.2): a place EXISTS
 * because it was logged, not because a row says so. So the "last place I
 * trained at" — which is what the chip offers by default — costs no storage
 * and cannot drift from the sets that define it.
 *
 * Deliberately NOT a generic recency helper shared with
 * knownEquipmentInstances: that one is per-exercise and carries a load
 * signature, this one is global and carries neither. Two call sites with
 * different rules buy nothing from one abstraction but indirection.
 */
export const knownLocations = (
  workouts: readonly Workout[],
): KnownLocation[] => {
  const byValue = new Map<string, KnownLocation>();
  for (const w of workouts) {
    for (const entry of w.entries) {
      const value = entry.location;
      if (!value) continue;
      const prev = byValue.get(value);
      if (!prev) {
        byValue.set(value, { value, lastUsedAt: w.startedAt, sessions: 1 });
        continue;
      }
      // Workouts arrive in no guaranteed order, so this compares rather
      // than assuming — same rule as the instance walk above.
      byValue.set(value, {
        value,
        lastUsedAt: laterIso(w.startedAt, prev.lastUsedAt),
        sessions: prev.sessions + 1,
      });
    }
  }
  return Array.from(byValue.values()).sort((a, b) =>
    b.lastUsedAt.localeCompare(a.lastUsedAt),
  );
};

// ⛔ There are NO preset places, and that is a decision (Antenore, 2026-08-13).
// A first version offered "Home" and "Gym" ready-made; he refused them on the
// device: *"non voglio posti gia' nominati, voglio Default e basta, poi Add
// place"*. It is the same objection that killed invented machine names — a
// label you did not choose is a label you do not recognise — and here it is
// sharper, because a place you never go to is noise in the one list that is
// supposed to be short. The only place that exists without being created is
// the unnamed default, and that is just `location === null`.

// ⭐ Opaque place identity — Antenore's design, 2026-08-13:
//   *"se la tabella tag è a parte, il tag diventa facilmente anonimizzabile:
//    il client mostra palestra1 al coach e Let's Go Fitness all'atleta"*
//
// The set carries an ID that says nothing, and the readable name lives apart,
// owned by the athlete. That is strictly better than hiding the tag: a coach
// keeps the signal that TWO SESSIONS HAPPENED AT DIFFERENT PLACES — which
// explains a load difference — while never learning where either one was.
//
// ⛔ The name store does NOT exist yet, so nothing mints these today; the
// place is still the slug of what the athlete typed. See EQ-1-PLACEID.
//
// Only the RECOGNISER lands now, and only because it is already live: the
// coach projection lets a location tag through solely when this returns true
// (coachReadProjection.ts). So the day ids start being minted the gate is
// already correct, and every free-text value written in the meantime stays
// protected forever. The minting counterpart belongs to the slice that mints
// — shipping it here would be an exported function with no caller.
export const isAutoLocationValue = (value: string): boolean =>
  /^place-([a-z]|\d+)$/.test(value);

/**
 * The three-case rule of the design doc (§8.3), as one decision so the two
 * pickers cannot answer it differently.
 *
 *   none    → nothing to say; one tap, exactly like today
 *   single  → apply it silently; the athlete is never asked
 *   choose  → two or more are plausible, and guessing would dirty the data
 */
export type InstanceResolution =
  | { kind: "none" }
  | { kind: "single"; instance: KnownInstance }
  | { kind: "choose"; instances: KnownInstance[] };

export const resolveInstanceChoice = (
  known: readonly KnownInstance[],
  /**
   * ⭐ EQ-1-DEFAULT-WINS (Antenore, 2026-08-17, riformulato da lui il 18/08:
   * *"taggare dovrebbe forkare l'attrezzo, non sostituirlo"*).
   *
   * Naming a machine ADDS it beside the Default; it never takes the Default's
   * place. Without this, the first machine an athlete ever names becomes that
   * exercise's silent default — even when the untagged Default carries years
   * of sets — because `named` deliberately excludes the Default from the count
   * (see the ⛔ on `surveyEquipmentInstances`). His words for the failure:
   * *"una macchina nuova NON PUÒ diventare il nuovo default. Dobedan non può
   * diventare un default."*
   *
   * ⛔ The cure is NOT counting the Default into `known` (§12.3): that would
   * make `single` become `choose` and raise a prompt on every add, which is
   * exactly what the 2026-08-14 decision existed to prevent. It turns the
   * SINGLE case into `none` instead — attach nothing, ask nothing, stay on the
   * Default — which is the only outcome that both leaves the history where it
   * is and adds no tap.
   *
   * ⚠️ Off by default, and that is deliberate: the swap-picker's `asks` flag
   * (`_useMachineChoice`) answers a different question — interrupt to ask,
   * rather than attach in silence — and folding this in there would grow a
   * prompt on every exercise that merely has an untagged past, i.e. nearly all
   * of them. Only the silent path passes it.
   */
  opts: {
    defaultCarriesHistory?: boolean;
    /**
     * ⭐ EQ-1-LASTHERE (Antenore, 2026-08-18) — where "here" is, when the
     * caller knows it. Given a place, the silent apply may only attach
     * something that was ALREADY USED at that place.
     *
     * ⛔ It does NOT filter: `known` arrives whole and every machine stays one
     * scroll away in the tray. It narrows what silence is allowed to do, which
     * is the opposite direction — a place that HIDES a machine was the dead
     * end refused on 2026-08-13.
     *
     * ⛔ And it never degrades into "last used, globally" when nothing was
     * used here: that reproduces the Dobedan case (§12.4) his rule was written
     * against. No use here ⇒ `none`, and `none` means attach nothing and ask
     * nothing.
     */
    here?: string;
    /** The untagged Default's own per-place recency, when it has history.
     *
     *  ⛔ Read as a THREE-valued answer, not a boolean, because the third value
     *  is the one that bites: the Default may have years of sets and no place
     *  on any of them (places arrived later than the logs — that is every
     *  athlete's real history today). "Was it used here?" is then genuinely
     *  UNANSWERABLE, and an unanswerable question is not a "no": reading it as
     *  one lets a freshly-named machine displace an invisible history, which
     *  is exactly the P1 `EQ-1-DEFAULT-WINS` closed. So silence is released
     *  only when the map POSITIVELY says otherwise. */
    defaultLastUsedHere?: Readonly<Record<string, string>>;
  } = {},
): InstanceResolution => {
  if (known.length === 0) return { kind: "none" };

  const here = opts.here;
  if (here !== undefined) {
    // ⚠️ Two or more known HERE still ASK. The place decides WHETHER silence
    // may speak, never WHICH of two machines it picks — that stays §13.4's
    // decision, because silently taking the most recent would mis-tag an
    // athlete who alternates two machines in the same gym. Ordering is where
    // recency-here pays off (see `surveyEquipmentInstances`), not silence.
    const usedHere = known.filter((k) => k.lastUsedHere?.[here] !== undefined);
    if (usedHere.length === 0) return { kind: "none" };
    // ⛔ `known` whole, not `usedHere`: what silence may attach is narrowed by
    // the place, what the athlete may CHOOSE never is. A tray that hides a
    // machine is the dead end refused on 2026-08-13.
    if (usedHere.length > 1) return { kind: "choose", instances: [...known] };
    const only = usedHere[0]!;

    // The Default runs in the same race — but only where its answer is known.
    const byPlace = opts.defaultLastUsedHere;
    const defaultHere = byPlace?.[here];
    if (defaultHere !== undefined) {
      // It WAS used here: the later of the two wins, and if that is the
      // Default then silence means staying on it.
      return defaultHere > only.lastUsedHere![here]!
        ? { kind: "none" }
        : { kind: "single", instance: only };
    }
    // No positive answer for THIS place ⇒ protected, full stop.
    //
    // ⛔ There was a third branch here — "the Default has places on its sets
    // and none of them is here, so it really was not used here" — and it was
    // wrong for a reason worth keeping written down: per-place coverage on the
    // Default is NEVER complete. Everything logged before places existed stays
    // invisible to `lastUsedHere` forever, so ONE tracked place is enough to
    // make every other place read as "positively not there" while years of
    // untagged history at that other place say otherwise. Absence of evidence,
    // dressed as evidence of absence.
    return opts.defaultCarriesHistory
      ? { kind: "none" }
      : { kind: "single", instance: only };
  }

  if (known.length === 1) {
    return opts.defaultCarriesHistory
      ? { kind: "none" }
      : { kind: "single", instance: known[0]! };
  }
  return { kind: "choose", instances: [...known] };
};
